import React, { useState, useEffect } from 'react';
import FileUpload from '../components/FileUpload';
import {
  AcademicCapIcon,
  SparklesIcon,
  CheckCircleIcon,
  TrashIcon,
  PlusIcon,
  StarIcon
} from '@heroicons/react/24/outline';

export default function Grading() {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [extractedFramework, setExtractedFramework] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gradingCriteria, setGradingCriteria] = useState([]);

  useEffect(() => {
    loadExistingFramework();
  }, []);

  const loadExistingFramework = async () => {
    try {
      const response = await fetch('/api/v1/zoomfabric/grading/framework');
      if (response.ok) {
        const data = await response.json();
        if (data.framework) {
          setExtractedFramework(data.framework);
          setGradingCriteria(data.framework.criteria || []);
        }
      }
    } catch (err) {
      console.error('Failed to load grading framework:', err);
    }
  };

  const handleFileUpload = async (files) => {
    const fileArray = Array.isArray(files) ? files : [files];
    setUploadedFiles(prev => [...prev, ...fileArray]);

    extractFramework([...uploadedFiles, ...fileArray]);
  };

  const extractFramework = async (files) => {
    setLoading(true);
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch('/api/v1/zoomfabric/grading/extract', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setExtractedFramework(data.framework);
        setGradingCriteria(data.framework.criteria || []);

        // Show feedback about augmentation
        if (data.augmented) {
          alert(`Framework successfully augmented! Updated to version ${data.framework.version}`);
        } else {
          alert('Grading framework extracted successfully!');
        }
      }
    } catch (err) {
      console.error('Failed to extract grading framework:', err);
    } finally {
      setLoading(false);
    }
  };

  const removeFile = (index) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);

    if (newFiles.length > 0) {
      extractFramework(newFiles);
    } else {
      setExtractedFramework(null);
      setGradingCriteria([]);
    }
  };

  const addCriterion = () => {
    const newCriterion = {
      id: Date.now().toString(),
      name: '',
      description: '',
      weight: 1,
      scale: 5,
      indicators: {
        excellent: '',
        good: '',
        fair: '',
        poor: ''
      }
    };
    setGradingCriteria([...gradingCriteria, newCriterion]);
  };

  const updateCriterion = (id, field, value) => {
    setGradingCriteria(criteria => criteria.map(crit =>
      crit.id === id ? { ...crit, [field]: value } : crit
    ));
  };

  const updateIndicator = (id, level, value) => {
    setGradingCriteria(criteria => criteria.map(crit =>
      crit.id === id ? {
        ...crit,
        indicators: { ...crit.indicators, [level]: value }
      } : crit
    ));
  };

  const deleteCriterion = (id) => {
    setGradingCriteria(criteria => criteria.filter(crit => crit.id !== id));
  };

  const saveFramework = async () => {
    setLoading(true);
    try {
      const framework = {
        ...extractedFramework,
        criteria: gradingCriteria
      };

      const response = await fetch('/api/v1/zoomfabric/grading/framework', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ framework })
      });

      if (response.ok) {
        alert('Grading framework saved successfully!');
      }
    } catch (err) {
      console.error('Failed to save grading framework:', err);
      alert('Failed to save grading framework');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Call Grading</h1>
        <p className="text-lg text-gray-600 mt-2">
          Upload training materials and grading rubrics to configure how calls should be evaluated
        </p>
      </div>

      {/* File Upload Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Grading Documentation</h3>
          <p className="text-sm text-gray-600 mb-4">
            Upload training materials, grading rubrics, performance standards, or any documents that describe how calls should be evaluated.
            {extractedFramework ? 'New uploads will augment and improve your existing framework.' : 'AI will extract grading criteria from your documents.'}
          </p>
          <FileUpload onFileSelect={handleFileUpload} multiple={true} />

          {uploadedFiles.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Files:</h4>
              <div className="space-y-2">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-sm text-gray-600">{file.name}</span>
                    <button
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Extracted Framework */}
      {loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6 text-center">
            <SparklesIcon className="w-8 h-8 text-blue-500 mx-auto mb-4 animate-pulse" />
            <p className="text-gray-600">AI is analyzing your grading documentation...</p>
          </div>
        </div>
      )}

      {extractedFramework && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-medium text-gray-900">Grading Framework</h3>
                {extractedFramework.version && (
                  <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                    v{extractedFramework.version}
                  </span>
                )}
                {extractedFramework.last_augmented && (
                  <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                    Recently Augmented
                  </span>
                )}
              </div>
              <button
                onClick={addCriterion}
                className="flex items-center px-3 py-2 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700"
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                Add Criterion
              </button>
            </div>

            {extractedFramework.description && (
              <div className="mb-4 p-4 bg-orange-50 rounded-lg">
                <p className="text-orange-900">{extractedFramework.description}</p>
              </div>
            )}

            {extractedFramework.augmentation_notes && (
              <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-start">
                  <SparklesIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-yellow-900">Latest Framework Updates</p>
                    <p className="text-sm text-yellow-800 mt-1">{extractedFramework.augmentation_notes}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {gradingCriteria.map((criterion, index) => (
                <div key={criterion.id || index} className={`border border-gray-200 rounded-lg p-4 ${
                  criterion.added_in_version ? 'border-green-300 bg-green-50' :
                  criterion.updated_in_version ? 'border-blue-300 bg-blue-50' : ''
                }`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center flex-1">
                      <input
                        type="text"
                        value={criterion.name}
                        onChange={(e) => updateCriterion(criterion.id || index, 'name', e.target.value)}
                        placeholder="Grading criterion name (e.g., Communication Skills, Problem Resolution)"
                        className="text-lg font-medium border-none bg-transparent focus:ring-0 focus:outline-none flex-1"
                      />
                      {criterion.added_in_version && (
                        <span className="ml-2 px-2 py-1 text-xs font-medium bg-green-200 text-green-800 rounded">
                          New in v{criterion.added_in_version}
                        </span>
                      )}
                      {criterion.updated_in_version && !criterion.added_in_version && (
                        <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-200 text-blue-800 rounded">
                          Updated in v{criterion.updated_in_version}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => deleteCriterion(criterion.id || index)}
                      className="text-red-500 hover:text-red-700 ml-4"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>

                  <textarea
                    value={criterion.description}
                    onChange={(e) => updateCriterion(criterion.id || index, 'description', e.target.value)}
                    placeholder="What does this criterion evaluate?"
                    rows={2}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm mb-4"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Weight (importance)</label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={criterion.weight || 1}
                        onChange={(e) => updateCriterion(criterion.id || index, 'weight', parseInt(e.target.value))}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Scale (1-5, 1-10, etc.)</label>
                      <input
                        type="number"
                        min="2"
                        max="10"
                        value={criterion.scale || 5}
                        onChange={(e) => updateCriterion(criterion.id || index, 'scale', parseInt(e.target.value))}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>

                  {/* Performance Indicators */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Performance Indicators</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-green-700 mb-1">
                          <StarIcon className="w-3 h-3 inline mr-1" />
                          Excellent Performance
                        </label>
                        <textarea
                          value={criterion.indicators?.excellent || ''}
                          onChange={(e) => updateIndicator(criterion.id || index, 'excellent', e.target.value)}
                          placeholder="What indicates excellent performance?"
                          rows={2}
                          className="w-full p-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-blue-700 mb-1">
                          Good Performance
                        </label>
                        <textarea
                          value={criterion.indicators?.good || ''}
                          onChange={(e) => updateIndicator(criterion.id || index, 'good', e.target.value)}
                          placeholder="What indicates good performance?"
                          rows={2}
                          className="w-full p-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-yellow-700 mb-1">
                          Fair Performance
                        </label>
                        <textarea
                          value={criterion.indicators?.fair || ''}
                          onChange={(e) => updateIndicator(criterion.id || index, 'fair', e.target.value)}
                          placeholder="What indicates fair performance?"
                          rows={2}
                          className="w-full p-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-red-700 mb-1">
                          Poor Performance
                        </label>
                        <textarea
                          value={criterion.indicators?.poor || ''}
                          onChange={(e) => updateIndicator(criterion.id || index, 'poor', e.target.value)}
                          placeholder="What indicates poor performance?"
                          rows={2}
                          className="w-full p-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={saveFramework}
                disabled={loading}
                className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                <CheckCircleIcon className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : 'Save Grading Framework'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex">
          <AcademicCapIcon className="w-6 h-6 text-gray-600 flex-shrink-0" />
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900 mb-2">How Grading Works</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Upload training materials, grading rubrics, or performance standards</li>
              <li>• AI will extract grading criteria and performance indicators</li>
              <li>• <strong>Framework Evolution:</strong> Upload additional materials to refine and enhance existing criteria</li>
              <li>• Review and customize the extracted/augmented framework</li>
              <li>• Set weights for different criteria based on importance</li>
              <li>• Define performance indicators for different score levels</li>
              <li>• Save the framework to use in call grading jobs</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}